export const MAX_RATING = 5;

// Highest star first - the order the breakdown is rendered in.
export const RATING_STARS = [5, 4, 3, 2, 1];

export interface IRatingDistributionFields {
  amazonfivestarcount?: number | null;
  amazonfourstarcount?: number | null;
  amazonthreestarcount?: number | null;
  amazontwostarcount?: number | null;
  amazononestarcount?: number | null;
  amazonratingtotalcount?: number | null;
}

export interface IRatingBucket {
  star: number;
  count: number;
  percentage: number;
}

export interface IRatingDistribution {
  // Amazon's own count, which can differ from the summed buckets by a rounding unit.
  total: number;
  buckets: IRatingBucket[];
}

const toCount = (value: unknown): number | null => {
  // Number(null) and Number('') are both 0, which would fake a zero-count bucket.
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const count = Number(value);

  return Number.isFinite(count) && count >= 0 ? Math.round(count) : null;
};

const getStarCounts = (book: IRatingDistributionFields): Array<number | null> => [
  toCount(book.amazonfivestarcount),
  toCount(book.amazonfourstarcount),
  toCount(book.amazonthreestarcount),
  toCount(book.amazontwostarcount),
  toCount(book.amazononestarcount)
];

/**
 * The star breakdown for a title, or null when it cannot be trusted: any count missing, or
 * all five zero - a rated title has ratings behind it, so five zeroes means an unsynced row.
 */
export const getRatingDistribution = (
  book?: IRatingDistributionFields | null
): IRatingDistribution | null => {
  if (!book) {
    return null;
  }

  const counts = getStarCounts(book);

  if (counts.some(count => count === null)) {
    return null;
  }

  const starCounts = counts as number[];
  const rated = starCounts.reduce((sum, count) => sum + count, 0);

  if (rated === 0) {
    return null;
  }

  return {
    total: toCount(book.amazonratingtotalcount) || rated,
    // Against the summed buckets, not the published total, so they always add up to ~100%.
    buckets: RATING_STARS.map((star, index) => ({
      star,
      count: starCounts[index],
      percentage: Math.round((starCounts[index] / rated) * 100)
    }))
  };
};

/** How much of a five-star run to paint, rounded to the nearest half star (4.7 -> 4.5). */
export const toStarFillPercentage = (rating: number): number => {
  const clamped = Math.min(Math.max(rating, 0), MAX_RATING);

  return ((Math.round(clamped * 2) / 2) / MAX_RATING) * 100;
};
